import React from 'react';
import { ChevronLeft } from 'lucide-react';
import Step4Result from './Step4Result';
import FlowPage from './components/FlowPage';
import GenerateTriggerPanel from './components/GenerateTriggerPanel';
import { useQuickGenFlow } from './hooks/useQuickGenFlow';
import AlertDialog from '../../components/AlertDialog';
import { RunningLoader } from '@/components/RunningLoader';

const QuickGen: React.FC = () => {
    const flow = useQuickGenFlow();

    return (
        <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">
            {/* 返回工具包列表 */}
            <button
                onClick={flow.handleReturnToList}
                className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 transition-colors -mb-4"
            >
                <ChevronLeft size={16} />
                返回工具包列表
            </button>

            {/* Header — 生成完成頁（Step 4）不顯示，節省垂直空間 */}
            {flow.genStep < 4 && (
                <header className="flex flex-col md:flex-row md:justify-between md:items-end gap-3">
                    <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            {/* 手機隱藏主標題（已由頂部列顯示） */}
                            <h2 className="hidden lg:block text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">揪里嗨 AI快速生成</h2>
                            {flow.appliedToolkit && (
                                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold border border-blue-200 dark:border-blue-700/50">
                                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                                    來自工具包：{flow.appliedToolkit.title}
                                </span>
                            )}
                        </div>
                        <p className="hidden lg:block text-slate-500 dark:text-slate-400">依照引導完成步驟，輕鬆製作專業里務公告。</p>
                    </div>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-32 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                style={{ width: `${flow.progressPercent}%` }}
                            />
                        </div>
                        <span className="text-sm font-semibold text-slate-500 dark:text-slate-400 w-10 text-right">
                            {flow.progressPercent}%
                        </span>
                    </div>
                </header>
            )}

            {/* Flow 問卷頁 */}
            {flow.genStep < 4 && flow.flowPageIndex < flow.flowPages.length && (
                <FlowPage
                    questions={flow.flowPages[flow.flowPageIndex]}
                    answers={flow.flowAnswers}
                    onAnswerChange={flow.handleAnswerChange}
                    ratios={flow.ratios}
                    styles={flow.styles}
                    tones={flow.tones}
                    onNext={() => flow.setFlowPageIndex(i => i + 1)}
                    onPrev={() => flow.setFlowPageIndex(i => i - 1)}
                    isFirstPage={flow.flowPageIndex === 0}
                    nextLabel={flow.flowPageIndex === flow.flowPages.length - 1 ? '下一步：確認生成' : undefined}
                />
            )}

            {/* 生成觸發面板 */}
            {flow.genStep < 4 && flow.flowPageIndex === flow.flowPages.length && (
                <GenerateTriggerPanel
                    flowModel={flow.flowModel}
                    flowAnswers={flow.flowAnswers}
                    ratios={flow.ratios}
                    styles={flow.styles}
                    tones={flow.tones}
                    isGenerating={flow.isMode2Generating}
                    onBack={() => flow.setFlowPageIndex(i => i - 1)}
                    onGenerate={() => flow.handleSubmitFlow(flow.flowAnswers)}
                />
            )}

            {/* Step 4: 結果展示 */}
            {flow.genStep === 4 && (
                <Step4Result
                    genConfig={flow.genConfig}
                    generatedResult={flow.generatedResult}
                    mode2Result={flow.mode2Result}
                    onBack={flow.handleBackFromResult}
                    globalProfile={flow.globalProfile}
                    name={flow.name}
                    title={flow.title}
                    organizationName={flow.organizationName}
                    organizationType={flow.organizationType}
                    onContinueToMode3={flow.handleContinueToMode3}
                    mode3InitialMessage={flow.mode3InitialMessage}
                    mode3InitialImageFile={flow.mode3InitialImageFile}
                    onRegenerateWithConfig={flow.handleRegenerateWithConfig}
                    onRegenerateWithImage={flow.handleRegenerateWithImage}
                    onRegenerateWithCurrentImage={flow.handleRegenerateWithCurrentImage}
                    isMode2Generating={flow.isMode2Generating}
                    isMode2GenerationFailed={flow.mode2GenerationFailed}
                    isMode2V2={true}
                    flowModel={flow.flowModel}
                    imageHistory={flow.imageHistory}
                    selectedHistoryIndex={flow.selectedHistoryIndex}
                    onSelectHistoryImage={flow.selectHistoryImage}
                    restoredInfo={flow.restoredInfo}
                    chatRestore={flow.chatRestore}
                    generationSessionId={flow.generationSessionId}
                    onChatSessionIdChange={flow.handleChatSessionIdChange}
                />
            )}

            {/* 點數不足對話框 — 來自 server 402 錯誤 */}
            {flow.pointsExhaustedMessage && (
                <AlertDialog
                    title="點數不足"
                    message={flow.pointsExhaustedMessage}
                    severity="warning"
                    onClose={flow.dismissPointsExhausted}
                />
            )}

            {/* 外部系統 deep link：AI 處理中 popup（不可關閉，redeem 完成後自動消失） */}
            {flow.isRedeemingPayload && (
                <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4 text-center">
                        <RunningLoader size="lg" label={null} className="mx-auto mb-4" />
                        <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-2">AI 正在解析活動資料</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                            系統正依照您提供的資訊自動填入欄位，請稍候…
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickGen;
